/* AttentionInbox · Constitutional Refactor · Batch C2b
 * =====================================================
 * The docked, universal surface for everything that needs a user's
 * attention. Competing prompts (NPS, licence renewals, uploads
 * waiting, licence checks, welcome tours, etc.) FUNNEL through
 * this inbox from C3 onwards — during C2b we lay the primitive so
 * later domain batches can migrate independent prompts here.
 *
 * Constitutional articles served:
 *   • 4 (Better Decisions) — attention is ranked, not scattered.
 *   • 7 (Reduce Cognitive Load) — one inbox, docked, not floating.
 *   • 9 (Consistency Builds Trust) — same behaviour every route.
 *
 * The inbox is intentionally quiet by default — an empty inbox is
 * rendered as a discreet "All clear" pill, never as a modal.
 * Priority-0 items may drive the surface's PrimaryAction; that
 * hand-off is the responsibility of the consuming surface (C3+).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Bell, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { CANONICAL_TRUTH_STATES } from "../hooks/useTruthState";
import ContinuityOptions from "./instructor/ContinuityOptions";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

function sanitiseItem(raw) {
  const item = raw && typeof raw === "object" ? raw : {};
  const state = CANONICAL_TRUTH_STATES.includes(item.state) ? item.state : null;
  return {
    id:            String(item.id || ""),
    source:        item.source,
    pupil_id:      item.context?.pupil_id,
    actor:         item.actor || "system",
    priority:      Number.isInteger(item.priority) ? item.priority : 3,
    state,
    title:         item.title || "",
    action_intent: item.action_intent || null,
    action_route:  item.action_route || null,
  };
}

export function AttentionInbox({
  compact = true,       // docked pill by default; can expand into a list
  endpoint = "/api/attention/inbox",
}) {
  const { user } = useAuth();
  const authedUserId = user?.id || user?._id || null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    // Aug 2026 · Production Stabilisation — the inbox lives inside
    // UniversalApplicationShell which also wraps the /login route.
    // Firing this call before auth completes guarantees a 401 and
    // pinned the "Inbox unavailable" flag until a full remount.
    // We now skip the call when we know nobody is authenticated.
    if (!authedUserId) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      const rows = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems(rows.map(sanitiseItem).sort((a, b) => a.priority - b.priority));
    } catch {
      // Truth Doctrine · never silently disappear. Present a
      // docked error state; do NOT crash the shell.
      setError("Inbox unavailable.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, authedUserId]);

  useEffect(() => {
    load();
    const refresh = () => { if (document.visibilityState !== "hidden") load(); };
    const timer = setInterval(refresh, 60000);
    document.addEventListener("visibilitychange", refresh);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [load]);

  const count = items.length;

  return (
    <div
      data-testid="attention-inbox"
      data-attention-count={count}
      className="rounded-md bg-white dark:bg-slate-950 px-2"
    >
      <button
        type="button"
        data-testid="attention-inbox-toggle"
        onClick={() => {
          if (!expanded || error) load();
          setExpanded((v) => error ? true : !v);
        }}
        className="w-full min-h-11 flex items-center gap-2 text-left"
        aria-label={error ? "Notifications unavailable. Retry" : `Notifications, ${count} needing attention`}
        aria-expanded={expanded}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
        ) : error ? (
          <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
        ) : count === 0 ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
        ) : (
          <Bell className="w-3.5 h-3.5 text-slate-500" />
        )}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
          {loading ? "Loading notifications…" : error ? "Notifications unavailable · retry" : count === 0 ? "Notifications · all clear" : `Notifications · ${count}`}
        </span>
        <span className="flex-1" />
      </button>
      {expanded && !loading && !error && count > 0 && (
        <ul data-testid="attention-inbox-list" className="max-h-[35dvh] overflow-y-auto mt-2 space-y-1 border-t border-slate-100 dark:border-slate-800 pt-2">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`attention-item-${it.id}`}
              data-attention-priority={it.priority}
            >
              <button
                type="button"
                data-testid={`attention-item-open-${it.id}`}
                disabled={!it.action_route}
                onClick={() => it.action_route && window.location.assign(it.action_route)}
                className="w-full min-h-10 text-[11px] flex items-center gap-2 text-left disabled:cursor-default"
                aria-label={it.action_route ? `Open ${it.title || it.id}` : (it.title || it.id)}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
                <span className="text-slate-700 dark:text-slate-200 whitespace-normal">{it.title || it.id}</span>
                {it.state && (
                  <span className="ml-auto rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {it.state.replace("_", " ")}
                  </span>
                )}
              </button>
              {it.source === "lesson_continuity" && it.pupil_id && <ContinuityOptions key={it.pupil_id} pupilId={it.pupil_id} />}
            </li>
          ))}
        </ul>
      )}
      {compact ? null : (
        // Reserved slot for surface-owned attention footers.
        <span data-testid="attention-inbox-slot" />
      )}
    </div>
  );
}

export default AttentionInbox;



